import React from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import classNames from 'classnames';
import { action, computed, observable } from 'mobx';

import type {
	OnChangeValue as RSOnChangeValue, PropsValue, StylesConfig, ClassNamesConfig,
	AriaOnFocus,
} from 'react-select';
import RSelect, {
	OptionProps, Props, components,
} from 'react-select';

import { DisplayType } from 'Views/Components/Models/Enums';
import InputWrapper from 'Views/Components/Inputs/InputWrapper';
import Spinner from 'Views/Components/Spinner/Spinner';
import * as uuid from 'uuid';

export interface SelectOption<I> {
	/** The value to display in the combobox item dropdown */
	display: React.ReactNode;
	/** The value to select */
	value: I | undefined;
	/** In a multi combobox can the value be removed */
	isFixed?: boolean;
	/** Is the item currently disabled */
	isDisabled?: boolean;
	/** Additional classes. */
	className?: string;
	/** Additional text with less emphasis. */
	description?: string;
	/** Additional options for the dropdown item */
	optionProps?: OptionProps;
}

export type OnChangeValue<I, isMulti extends boolean> = RSOnChangeValue<SelectOption<I>, isMulti>

interface InternalSelectProps<T, I, M extends boolean = false> {
	/** The model to change the attribute of */
	model: T;
	/** The property on the model to change */
	modelProperty: keyof T;
	/**
	 * Gets an identifying property from the value object. This must be a unique value and not an object.
	 * By default this will get the value object itself
	 */
	getOptionValue?: (option: SelectOption<I>) => undefined | boolean | number | string | I;
	/**
	 * A function to compare the model property to the selected option
	 * @param modelProperty The model property to compare
	 * @param option The option from the combobox
	 */
	optionEqualFunc?: (modelProperty: string | number | boolean | undefined | I, option: SelectOption<I>) => boolean;
	/** The to display around the combobox */
	label: string;
	/** Weather the label is visible */
	labelVisible?: boolean;
	/** The tooltip to display */
	tooltip?: string;
	/** The display type to use */
	displayType?: DisplayType;
	/** The classname for to combobox */
	className?: string;
	/** Raw props that are passed through to the react-select component */
	inputProps?: Props;
	/** The placeholder text when the combobox is empty */
	placeholder?: string;
	/** A list of errors that are to be displayed around the combobox */
	errors?: string | string[];
	/** If the combobox is isDisabled */
	isDisabled?: boolean;
	/** If the field is required */
	isRequired?: boolean;
	/** Override of the onChange function. Using this will remove the model binding logic of the component */
	onChange?: (
		newValue: OnChangeValue<I, M>,
	) => void;
	// /** Action to perform after the onChange method is called */
	onAfterChange?: (
		newValue: OnChangeValue<I, M>,
	) => void;
	/** Is the select value clearable */
	isClearable?: boolean;
	/** An optional button set next to the label */
	sideButton?: React.ReactNode;
	/** Is the select component a multi select */
	isMultiple?: M;
	/** Is the select component searchable */
	searchable?: boolean;
	name?: string;
}

export interface SyncSelectProps<T, I> extends InternalSelectProps<T, I> {
	/**
	 * The options on the dropdown
	 * An array of JSON objects that by default will have the form of {display: string, value: string}
	 *
	 * The key and value properties can be overriden with the getOptionLabel and getOptionValue props
	 */
	options: SelectOption<I>[];
	/** If the combobox is searchable */
	// searchable?: boolean | ((options: ComboboxOption<I>[], value: string) => ComboboxOption<I>[]);
	/** If the combobox is in a loading state */
	loading?: boolean;
}

export interface AsyncSelectProps<T, I> extends InternalSelectProps<T, I> {
	/**
	 * The options on the dropdown
	 * A function that returns an promise resolving to an array of JSON objects that by default will have the form of
	 * {display: string, value: string}
	 *
	 * The key and value properties can be overriden with the getOptionLabel and getOptionValue props
	 */
	options: (searchInput: string) => Promise<SelectOption<I>[]>;
	/**
	 * The initial options that are displayed in the combobox before any search occurs
	 */
	initialOptions?: () => Promise<SelectOption<I>[]>;
}

interface InternalSyncSelectProps<T, I> extends SyncSelectProps<T, I> {
	getOptionValue: (option: SelectOption<I>) => undefined | boolean | number | string | I;
}

interface InternalAsyncSelectProps<T, I> extends AsyncSelectProps<T, I> {
	getOptionValue: (option: SelectOption<I>) => undefined | boolean | number | string | any;
}

export type ISelectProps<T, I> = AsyncSelectProps<T, I> | SyncSelectProps<T, I>;

/**
 * A dropdown selection menu for selecting an item from a list of options
 */
@observer
export class Select<T, I> extends React.Component<ISelectProps<T, I>> {
	static defaultProps = {
		labelVisible: true,
		minSearchLength: 0,
	};

	private _reFetch?: () => void = undefined;
	public get reFetch() {
		if (this._reFetch) {
			return this._reFetch;
		}
		return _.noop;
	}

	private getOptionValue = (option: SelectOption<I>) => this.props.getOptionValue
		? this.props.getOptionValue(option)
		: option.value;

	public render() {
		const {
			label,
			tooltip,
			modelProperty,
			isRequired,
			labelVisible = true,
			options,
			className,
			errors,
			displayType,
			sideButton,
			inputProps,
		} = this.props;

		const inputWrapperId = inputProps?.id ? inputProps.id : uuid.v4();

		return (
			<InputWrapper
				className={classNames('input-group__dropdown', className)}
				label={label}
				id={inputWrapperId}
				inputId={_.snakeCase(label)}
				errors={errors}
				labelVisible={labelVisible}
				tooltip={tooltip}
				displayType={displayType}
				inputName={modelProperty.toString()}
				isRequired={isRequired}
				sideButton={sideButton}
			>
				{
					typeof options === 'function'
						? (
							<AsyncSelect
								{...{ ...this.props, inputProps: { id: inputWrapperId } }}
								ref={(ref: any) => { this._reFetch = ref?.reFetch; }}
								options={options}
								getOptionValue={this.getOptionValue}
							/>
						)
						: (
							<SyncSelect
								{...{ ...this.props, inputProps: { ...inputProps, id: inputWrapperId } }}
								options={options}
								getOptionValue={this.getOptionValue}
							/>
						)
				}
			</InputWrapper>
		);
	}
}

@observer
class SyncSelect<T, I> extends React.Component<InternalSyncSelectProps<T, I>> {
	public render() {
		const {
			inputProps,
			loading,
			searchable = true,
			modelProperty,
			onAfterChange,
			optionEqualFunc,
			model,
			isClearable,
			getOptionValue,
			placeholder,
			onChange,
			options,
			isDisabled,
			label,
			isMultiple,
			errors,
			name,
		} = this.props;
		return (
			<InnerSelect
				model={model}
				modelProperty={modelProperty}
				getOptionValue={getOptionValue}
				options={options as any}
				isLoading={loading}
				isSearchable={searchable}
				isMultiple={!!isMultiple}
				isClearable={isClearable}
				placeholder={placeholder}
				onOverrideChange={onChange}
				onAfterChange={onAfterChange}
				isDisabled={isDisabled}
				errors={errors}
				optionEqualFunc={optionEqualFunc}
				ariaLabel={inputProps?.['aria-label'] ? inputProps['aria-label'] : label}
				name={name}
				{...inputProps}
			/>
		);
	}
}

type loadingState = 'loading' | 'error' | 'done';

@observer
class AsyncSelect<T, I> extends React.Component<InternalAsyncSelectProps<T, I>> {
	@observable
	private requestState: loadingState = 'done';

	@observable
	private initialRequestState: loadingState = 'loading';

	@observable
	private options: SelectOption<I>[] = [];

	private lastSearchQuery = '';

	public reFetch = () => this.searchOptions(this.lastSearchQuery);

	@action
	private searchOptions = (data: string): Promise<void> => {
		this.requestState = 'loading';
		this.lastSearchQuery = data;
		return this.props.options(data)
			.then(this.onSearchSuccess)
			.catch(this.onSearchFail);
	}

	@action
	private onSearchSuccess = (options: SelectOption<I>[], initial?: boolean) => {
		this.options.length = 0;
		this.options.push(...options);
		if (initial) {
			this.initialRequestState = 'done';
		} else {
			this.requestState = 'done';
		}
	};

	@action
	private onSearchFail = (error: any, initial?: boolean) => {
		console.error(error);
		if (initial) {
			this.initialRequestState = 'error';
		} else {
			this.requestState = 'error';
		}
	};

	constructor(props: InternalAsyncSelectProps<T, I>) {
		super(props);

		const { initialOptions } = this.props;

		if (initialOptions) {
			initialOptions()
				.then(_.partial(this.onSearchSuccess, _, true))
				.catch(_.partial(this.onSearchFail, _, true));
		} else {
			this.onSearchSuccess([], true);
		}
	}

	public render() {
		const {
			inputProps,
			isClearable,
			onChange,
			modelProperty,
			onAfterChange,
			optionEqualFunc,
			model,
			getOptionValue,
			placeholder,
			isDisabled,
			label,
			isMultiple,
			errors,
			searchable = true,
		} = this.props;

		// This switch exhausts all types so it does not need a default
		// eslint-disable-next-line default-case
		switch (this.initialRequestState) {
			case 'loading':
				return <Spinner />;
			case 'error':
				return <div>There was an error loading the combobox data</div>;
			case 'done':
				return (
					<InnerSelect
						model={model}
						modelProperty={modelProperty}
						getOptionValue={getOptionValue}
						options={this.options as any}
						// onSearchChange={(e, data) => this.searchOptions(data.searchQuery)}
						placeholder={placeholder}
						isLoading={this.requestState === 'loading'}
						isSearchable={searchable}
						isDisabled={isDisabled}
						isClearable={isClearable}
						isMultiple={!!isMultiple}
						errors={errors}
						onOverrideChange={onChange}
						onAfterChange={onAfterChange}
						optionEqualFunc={optionEqualFunc}
						ariaLabel={inputProps?.['aria-label'] ? inputProps['aria-label'] : label}
						{...inputProps}
					/>
				);
		}
	}
}

interface InnerSelectProps<T, I, M extends boolean = false> extends RSelect {
	model: T;
	modelProperty: keyof T;
	options: SelectOption<I>[]
	getOptionValue: (option: SelectOption<I>) => undefined | boolean | number | string | I;
	onOverrideChange?: (
		newValue: OnChangeValue<I, M>,
	) => void;
	onAfterChange?: (
		newValue: OnChangeValue<I, M>,
	) => void;
	optionEqualFunc?: (value: string | number | boolean | undefined | I, option: SelectOption<I>) => boolean;
	isMultiple: M;
	isSearchable?: boolean;
	errors?: string | string[];
	ariaLabel: string;
	minSearchLength?: number;
	name?: string;
	id?: string;
}
@observer
class InnerSelect<T, I, M extends boolean = false> extends React.Component<InnerSelectProps<T, I, M >> {
	constructor(props: InnerSelectProps<T, I, M>) {
		super(props);
		this.state = {};
	}

	@computed
	private get selectedItem():PropsValue<SelectOption<I>> {
		const {
			modelProperty: modelPropertyProp,
			model,
			options,
			isMultiple,
		} = this.props;

		const modelProperty = model[modelPropertyProp as keyof T] as any;

		if (modelProperty === null || modelProperty === undefined) {
			return null;
		}

		if (isMultiple && Array.isArray(modelProperty)) {
			return options.filter(option => {
				return _.some(modelProperty, v => this.optionsEqual(v, option));
			});
		}

			// If there is a value already selected in the model then we want that one to be selected
		return options.find(option => {
			return this.optionsEqual(modelProperty, option);
		}) || null;
	}

	private set selectedItem(value: PropsValue<SelectOption<I>>) {
		const {
			modelProperty, model, options, getOptionValue,
		} = this.props;

		if (Array.isArray(value)) {
			model[modelProperty] = [] as unknown as typeof model[keyof typeof model];

			const selected = options
				.filter(option => {
					return _.some(value, modelProp => this.optionsEqual(modelProp, option));
				})
				.map(option => getOptionValue(option));

			if (Array.isArray(model[modelProperty])) {
				// @ts-ignore
				model[modelProperty].push(...selected);
			}
		} else {
			const selected = options.find(option => {
				return this.optionsEqual(getOptionValue(value as SelectOption<I>), option);
			});

			if (selected !== null && selected !== undefined) {
				model[modelProperty] = selected.value as unknown as typeof model[keyof typeof model];
			} else {
				model[modelProperty] = undefined as unknown as typeof model[keyof typeof model];
			}
		}
	}

	@action
	private onChange = (
		newValue: OnChangeValue<I, M>,
	) => {
		// If the onChange is overwritten then we should just use that one instead
		if (this.props.onOverrideChange) {
			return this.props.onOverrideChange(newValue);
		}

		this.selectedItem = newValue;

		// If there is any logic to be done after the change of the combobox, do it here
		if (this.props.onAfterChange) {
			this.props.onAfterChange(newValue);
		}
	};

	private optionsEqual(modelProp: string | number | boolean | undefined | I, option: SelectOption<I>) {
		const { getOptionValue, optionEqualFunc } = this.props;

		return optionEqualFunc ? optionEqualFunc(modelProp, option) : getOptionValue(option) === modelProp;
	}

	public render() {
		const {
			isMultiple, ariaLabel, isSearchable, errors, minSearchLength, options, id,
		} = this.props;

		const hasErrors = () => {
			if (Array.isArray(errors)) {
				if (errors.filter(err => !!err).length === 0) {
					return false;
				}
				return true;
			}

			return !!errors;
		};

		const defaultClassNames: ClassNamesConfig<SelectOption<I>, M> = {
			control: state => classNames(
				'select__control',
				state.options.length === 0 || state.isDisabled ? 'empty-options' : '',
				hasErrors() ? 'has-errors' : '',
			),
			option: state => classNames(
				'select__option',
				state.isSelected ? 'selected' : '',
			),
			placeholder: state => classNames(
				'select-placeholder',
				state.options.length === 0 ? 'empty-option-placeholder' : '',
				state.isDisabled ? 'disabled-placeholder' : '',
			),
			input: state => classNames('select__input'),
			valueContainer: state => classNames('select__value-container'),
			dropdownIndicator: state => classNames(
				'select__dropdown-indicator',
				state.options.length === 0 ? 'empty-option-dropdown-indicator' : '',
				state.isDisabled ? 'disabled-dropdown-indicator' : '',
			),
		};

		const styleConfigs: StylesConfig<SelectOption<I>, M> = {
			option: (base, state) => ({
				':hover': {
					backgroundColor: '#f4f4f4',
				},
			}),
		};

		const onFocus: AriaOnFocus<SelectOption<I>> = ({ focused, isDisabled }) => {
			return `You are currently focused on option ${focused.description}`;
		};

		return (
			<RSelect<SelectOption<I>, M>
				onChange={this.onChange}
				value={this.selectedItem}
				data-id={Array.isArray(this.selectedItem) ? this.selectedItem.join(',') : this.selectedItem}
				options={options}
				filterOption={(option, searchInput) => {
					const displayValue = option.data.display?.toString().toLowerCase();
					if (minSearchLength && displayValue && displayValue.length < minSearchLength) {
						return true;
					}
					if (displayValue && displayValue.includes(searchInput.toLowerCase())) {
						return true;
					}
					return false;
				}}
				className={classNames(
					'select_main',
				)}
				classNames={defaultClassNames}
				components={{
					IndicatorSeparator: () => null,
					NoOptionsMessage: () => (
						<p className="select__no-option-message">No results found.</p>
					),
				}}
				isMulti={isMultiple}
				styles={styleConfigs}
				aria-labelledby={id && !ariaLabel ? id : undefined}
				aria-label={ariaLabel}
				ariaLiveMessages={{ onFocus }}
				formatOptionLabel={((props: SelectOption<I>) => {
					const {
						display, description, isDisabled, className,
					} = props;
					return (
						<div className="select__option-container">
							<p className={
								classNames(
									'select__option-label',
									{ 'is-disabled': isDisabled },
									className,
								)
							}
							>{display}
							</p>
							<p className={classNames(
								'select__option-description',
								{ 'is-disabled': isDisabled },
							)}
							>{description}
							</p>
						</div>
					);
				})}
				{..._.omit(
					this.props,
					'model',
					'modelProperty',
					'getOptionValue',
					'onAfterChange',
					'optionEqualFunc',
					'aria-label',
					'selection',
					'onChange',
					'value',
					'data-id',
					'options',
					'className',
					'renderLabel',
					'isSearchable',
				)}
			/>
		);
	}
}
